import TextareaAutosize from 'react-textarea-autosize';
import styled, { css } from 'styled-components';

import { IconCross } from '@xing-com/icons';
import { mediaConfined, mediaWide } from '@xing-com/layout-tokens';
import {
  scale005,
  scale010,
  scale040,
  scale070,
  scale080,
  scale090,
  scale100,
  scale130,
  scale150,
  scale160,
  scale180,
  spaceL,
  spaceS,
  xdlColorA11yBorder,
  xdlColorBackground,
  xdlColorControlBorderSecondary,
  xdlColorControlBorder,
  xdlColorControlFill,
  xdlColorControlFillSecondary,
  xdlColorDisabledSoft,
  xdlColorDisabledStrong,
  xdlColorFocusRing,
  xdlColorHoverSecondary,
  xdlColorText,
  xdlColorTextTertiary,
} from '@xing-com/tokens';

import type { InputBarSize, InputBarVariant } from './input-bar.types';

type WrapperTProps = {
  $size?: InputBarSize;
  $sizeConfined?: InputBarSize;
  $sizeWide?: InputBarSize;
};

type TextareaTProps = {
  $iconSize: number;
  $isFilled: boolean;
  $showClearButton: boolean;
  $size?: InputBarSize;
  $sizeConfined?: InputBarSize;
  $sizeWide?: InputBarSize;
  $variant?: InputBarVariant;
};

type InputTProps = {
  $iconSize: number;
  $isFilled: boolean;
  $isIconSet?: boolean;
  $showClearButton: boolean;
  $size?: InputBarSize;
  $sizeConfined?: InputBarSize;
  $sizeWide?: InputBarSize;
  $variant?: InputBarVariant;
};

type IconContainerTProps = {
  $iconSize?: 18 | 24;
  $size?: InputBarSize;
  $sizeConfined?: InputBarSize;
  $sizeWide?: InputBarSize;
  $variant?: InputBarVariant;
};

type ClearButtonTProps = {
  $size?: InputBarSize;
  $sizeConfined?: InputBarSize;
  $sizeWide?: InputBarSize;
  $variant?: InputBarVariant;
};

export const Wrapper = styled.div<WrapperTProps>`
  position: relative;
  z-index: 0;
`;

// *** Base Styles ***
const baseStyles = css`
  appearance: none;
  display: block;
  line-height: 1.5;
  resize: none;
  width: 100%;

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${scale005};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: -${scale005};
  }

  &[disabled] {
    pointer-events: none;
  }
`;

// *** Variants ***
export const variants = {
  default: {
    base: css`
      background-color: ${xdlColorControlFillSecondary};
      border-color: ${xdlColorA11yBorder};
      border-style: solid;
      border-width: ${scale005};
      color: ${xdlColorTextTertiary};
      outline: none;

      &:focus {
        background-color: ${xdlColorControlFill};
        border-color: ${xdlColorControlBorder};
      }

      &:hover {
        background-color: ${xdlColorHoverSecondary};
        border-color: ${xdlColorControlBorderSecondary};

        @media (prefers-contrast: more) {
          border-width: ${scale005};
          box-shadow: inset 0 0 0 ${scale010} ${xdlColorControlBorderSecondary};
        }
      }

      &::placeholder {
        color: ${xdlColorTextTertiary};
        font-size: ${scale070};
      }

      &:-ms-input-placeholder {
        color: ${xdlColorTextTertiary};
        font-size: ${scale070};
      }

      &[disabled] {
        background-color: ${xdlColorDisabledSoft};
        color: ${xdlColorDisabledStrong};

        &::placeholder {
          color: ${xdlColorDisabledStrong};
        }

        &:-ms-input-placeholder {
          color: ${xdlColorDisabledStrong};
        }
      }
    `,
    filled: css`
      background-color: ${xdlColorControlFill};
      border-color: ${xdlColorControlBorder};
      border-style: solid;
      border-width: ${scale005};
      color: ${xdlColorText};

      &[disabled] {
        background-color: ${xdlColorControlFill};
        border-color: ${xdlColorDisabledSoft};
        color: ${xdlColorDisabledStrong};
      }
    `,
  },
  plain: {
    base: css`
      background-color: ${xdlColorControlFill};
      border-color: ${xdlColorA11yBorder};
      border-style: solid;
      border-width: ${scale010};
      color: ${xdlColorTextTertiary};
      outline: none;

      &:focus {
        background-color: ${xdlColorControlFill};
        border-color: ${xdlColorControlBorderSecondary};
        border-width: ${scale010};
        color: ${xdlColorText};
      }

      &:hover {
        background-color: ${xdlColorBackground};
        border-color: ${xdlColorControlBorderSecondary};
        border-width: ${scale010};
        color: ${xdlColorText};

        @media (prefers-contrast: more) {
          border-width: ${scale005};
          box-shadow: inset 0 0 0 ${scale010} ${xdlColorControlBorderSecondary};
        }
      }

      &::placeholder {
        color: ${xdlColorTextTertiary};
        font-size: ${scale070};
      }

      &:-ms-input-placeholder {
        color: ${xdlColorTextTertiary};
        font-size: ${scale070};
      }

      &[disabled] {
        background-color: ${xdlColorDisabledSoft};
        color: ${xdlColorDisabledStrong};

        &::placeholder {
          color: ${xdlColorDisabledStrong};
        }

        &:-ms-input-placeholder {
          color: ${xdlColorDisabledStrong};
        }
      }
    `,
    filled: css`
      background-color: ${xdlColorControlFill};
      color: ${xdlColorText};

      &[disabled] {
        background-color: ${xdlColorControlFill};
        color: ${xdlColorDisabledStrong};
      }
    `,
  },
};

/** Icons */
export const icons = {
  default: {
    leadingIcon: css`
      color: ${xdlColorText};

      [disabled] + & {
        color: ${xdlColorDisabledStrong};
      }
    `,
    trailingIcon: css`
      color: ${xdlColorText};

      &[disabled] {
        color: ${xdlColorDisabledStrong};
      }
    `,
  },
  plain: {
    leadingIcon: css`
      color: ${xdlColorText};

      [disabled] + & {
        color: ${xdlColorDisabledStrong};
      }
    `,
    trailingIcon: css`
      color: ${xdlColorText};

      &[disabled] {
        color: ${xdlColorDisabledStrong};
      }
    `,
  },
};

// *** Sizes ***
/** Fake paddings
 *  Fixed pixel values are used on each to keep the height
 *  on the textarea multiline which can’t have a fixed height. */
export const sizes = {
  small: {
    wrapper: css`
      height: ${scale130};
    `,
    input: css`
      border-radius: calc(${scale130} / 2);
      font-size: ${scale070};
      height: ${scale130};
      padding-block-end: 5px;
      padding-block-start: 5px;
      padding-inline-end: ${spaceL};
      padding-inline-start: ${spaceL};

      &::placeholder {
        font-size: ${scale070};
        font-weight: 500;
      }
    `,
    iconContainer: css`
      inset-inline-start: ${scale080};
    `,
    isIconSet: css`
      padding-inline-start: calc(${spaceL} + 18px + ${spaceS});
    `,
    showClearButton: css`
      padding-inline-end: calc(32px + ${spaceS});
    `,
    clearIcon: css`
      inset-block-start: 0;
    `,
  },
  medium: {
    wrapper: css`
      height: ${scale150};
    `,
    input: css`
      border-radius: calc(${scale150} / 2);
      font-size: ${scale080};
      height: ${scale150};
      padding-block-end: 10px;
      padding-block-start: 10px;
      padding-inline-end: ${scale090};
      padding-inline-start: ${scale090};

      &::placeholder {
        font-size: ${scale080};
        font-weight: 500;
      }
    `,
    iconContainer: css`
      height: 24px;
      inset-inline-start: ${scale090};
      width: 24px;

      & > svg {
        height: 24px;
        width: 24px;
      }
    `,
    isIconSet: css`
      padding-inline-start: calc(${scale090} + 24px + ${spaceS});
    `,
    showClearButton: css`
      padding-inline-end: calc(42px + ${spaceS});
    `,
    clearIcon: css`
      height: 38px;
      inset-block-start: 2px;
      width: 38px;

      & > svg {
        height: 24px;
        width: 24px;
      }
    `,
  },
  large: {
    wrapper: css`
      height: ${scale160};
    `,
    input: css`
      border-radius: calc(${scale160} / 2);
      font-size: ${scale080};
      height: ${scale160};
      padding-block-end: 13px;
      padding-block-start: 13px;
      padding-inline-end: ${scale100};
      padding-inline-start: ${scale100};

      &::placeholder {
        font-size: ${scale080};
        font-weight: 500;
      }
    `,
    iconContainer: css`
      height: 24px;
      inset-inline-start: ${scale100};
      width: 24px;

      & > svg {
        height: 24px;
        width: 24px;
      }
    `,
    isIconSet: css`
      padding-inline-start: calc(${scale100} + 24px + ${spaceS});
    `,
    showClearButton: css`
      padding-inline-end: calc(48px + ${spaceS});
    `,
    clearIcon: css`
      height: 38px;
      inset-block-start: 5px;
      width: 38px;

      & > svg {
        height: 24px;
        width: 24px;
      }
    `,
  },
  xlarge: {
    wrapper: css`
      height: ${scale180};
    `,
    input: css`
      border-radius: calc(${scale180} / 2);
      font-size: ${scale090};
      height: ${scale180};
      padding-block-start: 19px;
      padding-block-end: 19px;
      padding-inline-end: ${scale100};
      padding-inline-start: ${scale100};

      &::placeholder {
        font-size: ${scale090};
        font-weight: 500;
      }
    `,
    iconContainer: css`
      height: 24px;
      inset-inline-start: ${scale100};
      width: 24px;

      & > svg {
        height: 24px;
        width: 24px;
      }
    `,
    isIconSet: css`
      padding-inline-start: calc(${scale100} + 24px + ${spaceS});
    `,
    showClearButton: css`
      padding-inline-end: calc(48px + ${spaceS});
    `,
    clearIcon: css`
      height: 38px;
      inset-block-start: 11px;
      width: 38px;

      & > svg {
        height: 24px;
        width: 24px;
      }
    `,
  },
};

// *** Dynamic Styles ***
const commonPropsStyles = css<InputTProps | TextareaTProps>`
  ${(props) => css`
    ${props.$variant &&
    css`
      ${variants[props.$variant].base};
      ${props.$isFilled && variants[props.$variant].filled};
    `};

    ${props.$size &&
    css`
      ${sizes[props.$size].input};
    `};

    ${props.$sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${sizes[props.$sizeConfined].input};
      }
    `};

    ${props.$sizeWide &&
    css`
      @media ${mediaWide} {
        ${sizes[props.$sizeWide].input};
      }
    `};
  `};
`;

const inputPropsStyles = css<InputTProps>`
  ${(props) => css`
    ${props.$size &&
    css`
      ${props.$isIconSet && sizes[props.$size].isIconSet};
      ${props.$showClearButton && sizes[props.$size].showClearButton};
    `};

    ${props.$sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${props.$isIconSet && sizes[props.$sizeConfined].isIconSet};
        ${props.$showClearButton && sizes[props.$sizeConfined].showClearButton};
      }
    `};

    ${props.$sizeWide &&
    css`
      @media ${mediaWide} {
        ${props.$isIconSet && sizes[props.$sizeWide].isIconSet};
        ${props.$showClearButton && sizes[props.$sizeWide].showClearButton};
      }
    `};
  `};
`;

const textareaPropsStyles = css<TextareaTProps>`
  ${(props) => css`
    ${props.$size &&
    css`
      ${props.$showClearButton && sizes[props.$size].showClearButton};
    `};

    ${props.$sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${props.$showClearButton && sizes[props.$sizeConfined].showClearButton};
      }
    `};

    ${props.$sizeWide &&
    css`
      @media ${mediaWide} {
        ${props.$showClearButton && sizes[props.$sizeWide].showClearButton};
      }
    `};
  `};
`;

export const Input = styled.input<InputTProps>`
  ${baseStyles};
  ${commonPropsStyles};
  ${inputPropsStyles};
`;

export const Textarea = styled(TextareaAutosize)<TextareaTProps>`
  ${baseStyles};
  ${commonPropsStyles};
  ${textareaPropsStyles};
  height: auto;
  resize: none;
  width: 100%;
`;

// *** Leading Icon ***
export const IconContainer = styled.span<IconContainerTProps>`
  color: ${xdlColorText};
  inset-block-start: 50%;
  margin-inline-end: ${scale040};
  position: absolute;
  transform: translateY(-50%);

  ${(props) => css`
    ${props.$iconSize &&
    css`
      height: ${props.$iconSize}px;
      width: ${props.$iconSize}px;
    `};

    ${props.$variant && icons[props.$variant].leadingIcon};

    ${props.$size && sizes[props.$size].iconContainer};

    ${props.$sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${sizes[props.$sizeConfined].iconContainer};
      }
    `};

    ${props.$sizeWide &&
    css`
      @media ${mediaWide} {
        ${sizes[props.$sizeWide].iconContainer};
      }
    `};
  `};
`;

// *** Trailing Icon (Clear) ***
export const ClearButton = styled.button<ClearButtonTProps>`
  align-items: center;
  background: none;
  border: none;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  inset-inline-end: ${spaceS};
  isolation: isolate;
  justify-content: center;
  padding: calc(${spaceS} - 1px);
  position: absolute;
  z-index: 10;

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${scale005};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: -${scale005};
  }

  ${(props) => css`
    ${props.$variant && icons[props.$variant].trailingIcon};

    ${props.$size &&
    css`
      ${sizes[props.$size].clearIcon};
    `}

    ${props.$sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${sizes[props.$sizeConfined].clearIcon};
      }
    `}

    ${props.$sizeWide &&
    css`
      @media ${mediaWide} {
        ${sizes[props.$sizeWide].clearIcon};
      }
    `}
  `};
`;

export const Cross = styled(IconCross)`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
`;

export const VisuallyHiddenLabel = styled.label`
  clip-path: inset(50%);
  clip: rect(0 0 0 0);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;
